import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useGameStore, Team } from '@/features/game/store/gameStore';
import { useSubscriptionStore } from '@/features/game/store/subscriptionStore';
import { playerLifelineService, LifelineDto } from '@/features/game/services/lifelineService';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { LoadingSpinner } from '@/components/shared/LoadingSpinner';
import { PurchaseModal } from '@/components/shared/PurchaseModal';
import { ChevronDown, ChevronUp, Minus, Plus, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { API_BASE_URL } from '@/lib/api/apiClient';

const API_URL = API_BASE_URL || '';
const ruqaaFont = { fontFamily: "'Aref Ruqaa', 'Amiri', serif" };
const arabicFont = { fontFamily: "'Almarai', 'Cairo', sans-serif" };

// وسائل المساعدة: الأربعة مفعلة افتراضياً، والاستخدام الفعلي 3 فقط من أصل 4
const LIFELINES_USED_IN_GAME = 3;

// Helper to get full image URL
const getImageUrl = (path?: string) => {
  if (!path) return null;
  if (path.startsWith('http')) return path;
  const base = API_URL || '';
  const p = path.startsWith('/') ? path : `/${path}`;
  return base ? `${base}${p}` : p;
};

export const TeamSetupPage = () => {
  const navigate = useNavigate();
  const { gameSettings, setTeam1, setTeam2, initializeGameBoard, setGameSettings } = useGameStore();
  const { status: subscriptionStatus, useFreeTrial, useGame, fetchStatus } = useSubscriptionStore();
  
  const [lifelines, setLifelines] = useState<LifelineDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [isStarting, setIsStarting] = useState(false);
  const [showPurchaseModal, setShowPurchaseModal] = useState(false);
  
  // Game name
  const [gameName, setGameName] = useState('');
  
  // Team 1 state
  const [team1Name, setTeam1Name] = useState('');
  const [team1Lifelines, setTeam1Lifelines] = useState<string[]>([]);
  
  // Team 2 state
  const [team2Name, setTeam2Name] = useState('');
  const [team2Lifelines, setTeam2Lifelines] = useState<string[]>([]);
  
  // Player division
  const [showDivision, setShowDivision] = useState(false);
  const [playerCount, setPlayerCount] = useState(2);
  const [playerNames, setPlayerNames] = useState<string[]>(['', '']);

  // Load lifelines and subscription status
  useEffect(() => {
    loadLifelines();
    fetchStatus();
  }, []);

  // Redirect if no game settings
  useEffect(() => {
    if (!loading && (!gameSettings || gameSettings.categories.length === 0)) {
      navigate('/player/game-settings');
    }
  }, [gameSettings, navigate, loading]);

  const loadLifelines = async () => {
    try {
      setLoading(true);
      const data = await playerLifelineService.getEnabledLifelines();
      setLifelines(data);
      // افتراضياً: أول 3 من أصل 4 لكل فريق
      const defaultIds = data.slice(0, LIFELINES_USED_IN_GAME).map((l) => l.id);
      setTeam1Lifelines(defaultIds.length >= LIFELINES_USED_IN_GAME ? defaultIds : data.map((l) => l.id).slice(0, LIFELINES_USED_IN_GAME));
      setTeam2Lifelines(defaultIds.length >= LIFELINES_USED_IN_GAME ? defaultIds : data.map((l) => l.id).slice(0, LIFELINES_USED_IN_GAME));
    } catch (error) {
      console.error('Failed to load lifelines:', error);
    } finally {
      setLoading(false);
    }
  };

  // اختيار 3 من 4: تبديل اختيار وسيلة مساعدة (الحد الأقصى 3)
  const toggleTeam1Lifeline = (id: string) => {
    setTeam1Lifelines((prev) => {
      if (prev.includes(id)) return prev.filter((x) => x !== id);
      if (prev.length >= LIFELINES_USED_IN_GAME) return prev;
      return [...prev, id];
    });
  };
  const toggleTeam2Lifeline = (id: string) => {
    setTeam2Lifelines((prev) => {
      if (prev.includes(id)) return prev.filter((x) => x !== id);
      if (prev.length >= LIFELINES_USED_IN_GAME) return prev;
      return [...prev, id];
    });
  };

  // Update player count
  const updatePlayerCount = (delta: number) => {
    const newCount = Math.max(2, Math.min(10, playerCount + delta));
    setPlayerCount(newCount);
    
    // Adjust player names array
    const newNames = [...playerNames];
    while (newNames.length < newCount) newNames.push('');
    while (newNames.length > newCount) newNames.pop();
    setPlayerNames(newNames);
  };

  // Update player name
  const updatePlayerName = (index: number, name: string) => {
    const newNames = [...playerNames];
    newNames[index] = name;
    setPlayerNames(newNames);
  };

  // Validate: أسماء الفرق + كل فريق يختار 3 من 4 وسائل مساعدة
  const isValid =
    team1Name.trim() !== '' &&
    team2Name.trim() !== '' &&
    team1Lifelines.length === LIFELINES_USED_IN_GAME &&
    team2Lifelines.length === LIFELINES_USED_IN_GAME;

  // Check if this is a free trial game (default to true if no subscription info)
  const isFreeTrial = gameSettings?.isFreeTrial ?? true;
  
  // Handle start game
  const handleStartGame = async () => {
    if (!isValid || isStarting) return;

    setIsStarting(true);

    try {
      // If subscription status not loaded or user can play free trial
      if (!subscriptionStatus || isFreeTrial || subscriptionStatus.canPlayFree || !subscriptionStatus.hasUsedFreeTrial) {
        // Use free trial - try to call API but don't block if it fails
        try {
          await useFreeTrial();
          toast.success('تم تفعيل التجربة المجانية!');
        } catch (error) {
          console.log('Free trial API not available, continuing anyway');
        }
      } else if (subscriptionStatus.gamesRemaining && subscriptionStatus.gamesRemaining > 0) {
        // Use a purchased game
        const success = await useGame();
        if (!success) {
          // No games remaining, need to purchase
          setShowPurchaseModal(true);
          setIsStarting(false);
          return;
        }
        toast.success('تم خصم لعبة واحدة');
      } else {
        // Already used free trial and no games remaining
        setShowPurchaseModal(true);
        setIsStarting(false);
        return;
      }

      // وسائل المساعدة: 3 من 4 لكل فريق (كما اختاروا)
      const team1LifelineObjects = lifelines.filter((l) => team1Lifelines.includes(l.id));
      const team2LifelineObjects = lifelines.filter((l) => team2Lifelines.includes(l.id));

      // Create team objects
      const newTeam1: Team = {
        id: 'team1',
        name: team1Name.trim(),
        memberCount: Math.ceil(playerCount / 2),
        score: 0,
        color: '#DC2626', // Red
        usedLifelines: [],
        lifelines: team1LifelineObjects,
      };

      const newTeam2: Team = {
        id: 'team2',
        name: team2Name.trim(),
        memberCount: Math.floor(playerCount / 2),
        score: 0,
        color: '#16A34A', // Green
        usedLifelines: [],
        lifelines: team2LifelineObjects,
      };

      // Store teams in game store
      setTeam1(newTeam1);
      setTeam2(newTeam2);
      
      // تحديث إعدادات اللعبة: الاسم + الوسائل الأربع (حتى تظهر على اللوحة وداخل السؤال)
      if (gameSettings) {
        setGameSettings({
          ...gameSettings,
          gameName: gameName.trim() || 'لعبة جديدة',
          lifelines: lifelines,
        });
      }
      
      // Initialize the game board
      initializeGameBoard();

      // Navigate to game board
      navigate('/player/game');
    } catch (error) {
      console.error('Failed to start game:', error);
      toast.error('حدث خطأ أثناء بدء اللعبة');
    } finally {
      setIsStarting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-white">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" style={arabicFont}>
      <div className="container mx-auto max-w-4xl px-4 py-8">
        {/* Free Trial Banner */}
        {isFreeTrial && (
          <div className="mb-6 bg-gradient-to-r from-green-500 to-emerald-600 rounded-2xl p-4 text-white shadow-lg">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
                <Sparkles className="w-6 h-6" />
              </div>
              <div>
                <h3 className="font-bold text-lg">تجربتك المجانية</h3>
                <p className="text-green-100 text-sm">
                  هذه لعبتك المجانية الوحيدة، استمتع!
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Title */}
        <h1 className="text-4xl md:text-5xl font-bold text-center text-gray-800 mb-8" style={ruqaaFont}>
          حدد معلومات الفرق
        </h1>

        {/* Game Name Input */}
        <div className="max-w-md mx-auto mb-8">
          <Input
            value={gameName}
            onChange={(e) => setGameName(e.target.value)}
            placeholder="اسم اللعبة"
            className="text-center text-lg border-2 border-gray-300 rounded-lg py-3"
          />
          {!gameName.trim() && (
            <p className="text-red-500 text-xs text-center mt-1">الرجاء إدخال اسم اللعبة</p>
          )}
        </div>

        {/* Teams Section */}
        <div className="grid md:grid-cols-2 gap-8 mb-8">
          {/* Team 1 */}
          <div className="space-y-4">
            <h2 className="text-2xl font-bold text-center text-gray-800" style={ruqaaFont}>
              الفريق الأول
            </h2>
            
            {/* Team Name Input */}
            <div>
              <Input
                value={team1Name}
                onChange={(e) => setTeam1Name(e.target.value)}
                placeholder="اسم الفريق"
                className="text-center text-lg border-2 border-gray-300 rounded-lg py-3"
              />
              {!team1Name.trim() && (
                <p className="text-red-500 text-xs text-center mt-1">الرجاء إدخال اسم الفريق الأول</p>
              )}
            </div>

            {/* وسائل المساعدة: اختر 3 من 4 لهذا الفريق */}
            <div>
              <p className="text-gray-600 text-center mb-2 text-sm">
                وسائل المساعدة: اختر {LIFELINES_USED_IN_GAME} من {lifelines.length} (الحفرة قبل السؤال، الباقي من داخل السؤال)
              </p>
              <div className="flex justify-center gap-2 flex-wrap">
                {lifelines.map((lifeline) => {
                  const selected = team1Lifelines.includes(lifeline.id);
                  const atLimit = team1Lifelines.length >= LIFELINES_USED_IN_GAME && !selected;
                  return (
                    <button
                      key={lifeline.id}
                      type="button"
                      onClick={() => toggleTeam1Lifeline(lifeline.id)}
                      disabled={atLimit}
                      className={`flex flex-col items-center p-2 rounded-xl border-2 transition-all ${
                        selected ? 'bg-red-100 border-red-500 ring-2 ring-red-300' : atLimit ? 'bg-gray-100 border-gray-200 opacity-60 cursor-not-allowed' : 'bg-gray-50 border-gray-200 hover:border-red-300'
                      }`}
                    >
                      <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-1 overflow-hidden" style={{ backgroundColor: selected ? `${lifeline.iconColor}30` : `${lifeline.iconColor}20`, border: `2px solid ${lifeline.iconColor || '#9CA3AF'}` }}>
                        {getImageUrl(lifeline.iconUrl) ? (
                          <img src={getImageUrl(lifeline.iconUrl)!} alt={lifeline.nameAr} className="w-8 h-8 object-contain" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                        ) : (
                          <span style={{ color: lifeline.iconColor, fontSize: '1.25rem' }}>⭐</span>
                        )}
                      </div>
                      <span className="text-[10px] font-medium text-gray-600 truncate max-w-[64px]">{lifeline.nameAr}</span>
                      {selected && <span className="text-[10px] text-red-600 font-bold">✓</span>}
                    </button>
                  );
                })}
              </div>
              <p className="text-center text-xs text-gray-500 mt-1">{team1Lifelines.length} / {LIFELINES_USED_IN_GAME} مختار</p>
            </div>
          </div>

          {/* Team 2 */}
          <div className="space-y-4">
            <h2 className="text-2xl font-bold text-center text-gray-800" style={ruqaaFont}>
              الفريق الثاني
            </h2>
            
            {/* Team Name Input */}
            <div>
              <Input
                value={team2Name}
                onChange={(e) => setTeam2Name(e.target.value)}
                placeholder="اسم الفريق"
                className="text-center text-lg border-2 border-gray-300 rounded-lg py-3"
              />
              {!team2Name.trim() && (
                <p className="text-red-500 text-xs text-center mt-1">الرجاء إدخال اسم الفريق الثاني</p>
              )}
            </div>

            {/* وسائل المساعدة: اختر 3 من 4 لهذا الفريق */}
            <div>
              <p className="text-gray-600 text-center mb-2 text-sm">
                وسائل المساعدة: اختر {LIFELINES_USED_IN_GAME} من {lifelines.length} (الحفرة قبل السؤال، الباقي من داخل السؤال)
              </p>
              <div className="flex justify-center gap-2 flex-wrap">
                {lifelines.map((lifeline) => {
                  const selected = team2Lifelines.includes(lifeline.id);
                  const atLimit = team2Lifelines.length >= LIFELINES_USED_IN_GAME && !selected;
                  return (
                    <button
                      key={lifeline.id}
                      type="button"
                      onClick={() => toggleTeam2Lifeline(lifeline.id)}
                      disabled={atLimit}
                      className={`flex flex-col items-center p-2 rounded-xl border-2 transition-all ${
                        selected ? 'bg-green-100 border-green-500 ring-2 ring-green-300' : atLimit ? 'bg-gray-100 border-gray-200 opacity-60 cursor-not-allowed' : 'bg-gray-50 border-gray-200 hover:border-green-300'
                      }`}
                    >
                      <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-1 overflow-hidden" style={{ backgroundColor: selected ? `${lifeline.iconColor}30` : `${lifeline.iconColor}20`, border: `2px solid ${lifeline.iconColor || '#9CA3AF'}` }}>
                        {getImageUrl(lifeline.iconUrl) ? (
                          <img src={getImageUrl(lifeline.iconUrl)!} alt={lifeline.nameAr} className="w-8 h-8 object-contain" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                        ) : (
                          <span style={{ color: lifeline.iconColor, fontSize: '1.25rem' }}>⭐</span>
                        )}
                      </div>
                      <span className="text-[10px] font-medium text-gray-600 truncate max-w-[64px]">{lifeline.nameAr}</span>
                      {selected && <span className="text-[10px] text-green-600 font-bold">✓</span>}
                    </button>
                  );
                })}
              </div>
              <p className="text-center text-xs text-gray-500 mt-1">{team2Lifelines.length} / {LIFELINES_USED_IN_GAME} مختار</p>
            </div>
          </div>
        </div>

        {/* Player Division Section */}
        <div className="bg-gray-100 rounded-2xl overflow-hidden mb-8">
          <button
            onClick={() => setShowDivision(!showDivision)}
            className="w-full flex items-center justify-between px-6 py-4 hover:bg-gray-200 transition-colors"
          >
            <span className="text-xl font-bold text-gray-800" style={ruqaaFont}>قسملي الفرق</span>
            {showDivision ? (
              <ChevronUp className="w-6 h-6 text-orange-500" />
            ) : (
              <ChevronDown className="w-6 h-6 text-orange-500" />
            )}
          </button>
          
          {showDivision && (
            <div className="px-6 py-6 space-y-6">
              {/* Player Count */}
              <div className="text-center">
                <p className="text-gray-700 font-bold mb-4">عدد اللاعبين</p>
                <div className="flex items-center justify-center gap-4">
                  <button
                    onClick={() => updatePlayerCount(-1)}
                    disabled={playerCount <= 2}
                    className="w-10 h-10 bg-white border-2 border-gray-300 rounded-full flex items-center justify-center text-gray-600 hover:bg-gray-50 disabled:opacity-50"
                  >
                    <Minus className="w-5 h-5" />
                  </button>
                  <span className="text-2xl font-bold text-gray-800 min-w-[50px] text-center">{playerCount}</span>
                  <button
                    onClick={() => updatePlayerCount(1)}
                    disabled={playerCount >= 10}
                    className="w-10 h-10 bg-white border-2 border-gray-300 rounded-full flex items-center justify-center text-gray-600 hover:bg-gray-50 disabled:opacity-50"
                  >
                    <Plus className="w-5 h-5" />
                  </button>
                </div>
              </div>

              {/* Player Names */}
              <div className="flex flex-wrap justify-center gap-3">
                {playerNames.map((name, index) => (
                  <Input
                    key={index}
                    value={name}
                    onChange={(e) => updatePlayerName(index, e.target.value)}
                    placeholder="اسم اللاعب"
                    className="w-32 text-center border-2 border-gray-300 rounded-lg"
                  />
                ))}
              </div>

              {/* Divide Button */}
              <div className="text-center">
                <Button
                  className="bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white px-8 py-3 rounded-full font-bold"
                >
                  عطني التقسيمه
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* Start Game Button */}
        <div className="text-center pb-8">
          <Button
            onClick={handleStartGame}
            disabled={!isValid || isStarting}
            className={`px-16 py-6 text-2xl font-bold rounded-full transition-all ${
              isValid && !isStarting
                ? 'bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white shadow-xl hover:shadow-2xl transform hover:scale-105'
                : 'bg-gray-400 text-gray-200 cursor-not-allowed'
            }`}
          >
            {isStarting ? 'جاري البدء...' : (isFreeTrial ? 'ابدأ التجربة المجانية' : 'ابدأ اللعب')}
          </Button>
          
          {!isValid && (
            <p className="text-gray-500 mt-4 text-sm">
              {!team1Name.trim() && 'أدخل اسم الفريق الأول • '}
              {!team2Name.trim() && 'أدخل اسم الفريق الثاني'}
            </p>
          )}
        </div>
      </div>

      {/* Purchase Modal */}
      <PurchaseModal
        isOpen={showPurchaseModal}
        onClose={() => setShowPurchaseModal(false)}
        reason="free_trial_used"
        gamesRemaining={subscriptionStatus?.gamesRemaining || 0}
      />
    </div>
  );
};
